import type { RouteMapPoints, ViaMapPoint } from 'frontend/all-demands/types/i-computed-data'
import { waypointIconFactory } from 'frontend/common/maps/fifa-gis/factories/icon-factories'
import { computed, type Ref } from 'vue'

export function isRequestedStop(viaPoint: ViaMapPoint): boolean {
  return viaPoint.subType && viaPoint.subType === 'CUSTOM_WAYPOINT' && !viaPoint.id
}

export function toRequestedStopMarker(viaPoint: ViaMapPoint, i: number) {
  const title = `Stop point #${i + 1}`
  return {
    id: viaPoint.id || i,
    type: 'Point',
    coordinates: viaPoint.pointGeo.coordinates?.toReversed(),
    infoPopoverContent: title,
    title,
    infoPopoverTrigger: 'hover',
    icon: waypointIconFactory({ label: `S${i + 1}` }),
  }
}

export function isMandatoryStop(viaPoint: ViaMapPoint): boolean {
  return viaPoint.subType && viaPoint.subType !== 'CUSTOM_WAYPOINT' && !!viaPoint.id
}

export function toMandatoryStopMarker(mandatoryPoint: ViaMapPoint, i: number) {
  const title = mandatoryPoint.name || `Mandatory Point #${i + 1}`
  return {
    id: mandatoryPoint.id || i,
    type: 'Point',
    coordinates: mandatoryPoint.pointGeo.coordinates?.toReversed(),
    infoPopoverContent: title,
    title,
    infoPopoverTrigger: 'hover',
    icon: waypointIconFactory({
      label: `M${i + 1}`,
      strokeColor: '#fc9403',
      labelStyle: {
        fontColor: '#fc9403',
      },
    }),
  }
}

export function isRulePoint(viaPoint: ViaMapPoint): boolean {
  return !!viaPoint.id && !!viaPoint.ruleId && viaPoint.subType === 'CUSTOM_WAYPOINT'
}

export function toRuleMarker(rulePoint: ViaMapPoint, i: number) {
  const title = rulePoint.ruleName || `Rule Point #${i + 1}`
  return {
    id: rulePoint.id || i,
    ruleId: rulePoint.ruleId,
    type: 'Point',
    coordinates: rulePoint.pointGeo.coordinates?.toReversed(),
    infoPopoverContent: title,
    title,
    infoPopoverTrigger: 'hover',
    icon: waypointIconFactory({
      label: `R${i + 1}`,
      strokeColor: '#34d5eb',
      labelStyle: {
        fontColor: '#34d5eb',
      },
    }),
  }
}

export function extractRequestedStops(viaPoints: ViaMapPoint[]) {
  return viaPoints.filter(isRequestedStop).map(toRequestedStopMarker)
}

export function extractMandatoryStops(viaPoints: ViaMapPoint[]) {
  return viaPoints.filter(isMandatoryStop).map(toMandatoryStopMarker)
}

export function extractRulePoints(viaPoints: ViaMapPoint[]) {
  return viaPoints.filter(isRulePoint).map(toRuleMarker)
}

export function useMapPointsToMarkers(mapPoints: Ref<RouteMapPoints>) {
  const viaStopMarkers = computed(() => {
    if (!mapPoints.value?.via?.length) return []

    return extractRequestedStops(mapPoints.value.via)
  })

  const mandatoryWaypointMarkers = computed(() => {
    if (!mapPoints.value?.via?.length) return []

    return extractMandatoryStops(mapPoints.value.via)
  })

  const ruleMarkers = computed(() => {
    if (!mapPoints.value?.via?.length) return []

    return extractRulePoints(mapPoints.value.via)
  })

  return {
    viaStopMarkers,
    mandatoryWaypointMarkers,
    ruleMarkers,
  }
}
